/**
 * GPU time per frame from timestamp queries.
 *
 * Frame intervals from requestAnimationFrame are capped by vsync: a device
 * with plenty of headroom still reports 16.7 ms on a 60 Hz display. GPU
 * timestamps measure the work itself, so the governor can see headroom.
 *
 * Availability (checked after renderer.init()):
 * - WebGPU: the 'timestamp-query' feature. Not exposed on every device.
 * - WebGL2: EXT_disjoint_timer_query_webgl2. Often missing on mobile and Safari.
 * three.js r186 turns tracking off by itself when the feature is missing;
 * `available` reflects that.
 *
 * Queries are resolved every RESOLVE_EVERY frames, not every frame. Each
 * resolve is async and allocates a promise, so this is a small, periodic
 * exception to the zero-allocation rule (about 4 per second at 60 fps).
 */
import type { WebGPURenderer } from 'three/webgpu';

const RESOLVE_EVERY = 15;

export interface GpuTimer {
  readonly available: boolean;
  /** Mean GPU ms per frame (render + compute) over the last resolve window; NaN if none yet. */
  readonly gpuMs: number;
  /** Call once per rendered frame. */
  frameEnd(): void;
}

export function createGpuTimer(renderer: WebGPURenderer): GpuTimer {
  const backend = renderer.backend as {
    trackTimestamp?: boolean;
    isWebGLBackend?: boolean;
    disjoint?: unknown;
  };
  const available =
    backend.trackTimestamp === true &&
    (backend.isWebGLBackend !== true || backend.disjoint != null);

  let gpuMs = Number.NaN;
  let frames = 0;
  let pending = false;

  function resolve(n: number): void {
    pending = true;
    Promise.all([
      renderer.resolveTimestampsAsync('render'),
      renderer.resolveTimestampsAsync('compute'),
    ]).then(
      ([r, c]) => {
        const total = (typeof r === 'number' ? r : 0) + (typeof c === 'number' ? c : 0);
        if (total > 0) gpuMs = total / n;
        pending = false;
      },
      () => {
        pending = false;
      },
    );
  }

  return {
    available,
    get gpuMs(): number {
      return gpuMs;
    },
    frameEnd(): void {
      if (!available) return;
      frames++;
      if (frames >= RESOLVE_EVERY && !pending) {
        resolve(frames);
        frames = 0;
      }
    },
  };
}

/**
 * The value the governor observes.
 *
 * - If the frame interval is over budget, frames are being dropped: use the
 *   interval, which includes every source of delay.
 * - Otherwise the interval is vsync-capped and hides headroom: use the work
 *   time, max(CPU ms, GPU ms). max assumes CPU and GPU overlap (pipelined);
 *   if they ran strictly in series the true cost would be closer to the sum.
 * - Without GPU timing, fall back to the interval. The governor then cannot
 *   upgrade past what vsync shows; that limit is documented, not hidden.
 */
export function governorSignalMs(
  intervalMs: number,
  cpuMs: number,
  gpuMs: number,
  targetMs: number,
): number {
  if (intervalMs > targetMs * 1.05) return intervalMs;
  if (Number.isNaN(gpuMs)) return intervalMs;
  return cpuMs > gpuMs ? cpuMs : gpuMs;
}
